import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useMixpanel } from "react-mixpanel-browser";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../../../shared/header";
import { stepActions } from "../../../store/Steps";
import { useTranslation } from "react-i18next";
import { makePostRequest } from "../../../services/apiHandler";
import Loader from "../../../shared/loader/Loader";

function Callback() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const mixpanel = useMixpanel();
    const navigate = useNavigate();
    const tenantCreds = useSelector((state: any) => state.tenantCreds);
    const SKEY = sessionStorage.getItem("SKEY") || "";

    const [status, setStatus] = useState<"idle" | "verifying" | "success" | "failed">("idle");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const getQuoteIdFromUrl = (): string | null => {
        const hash = window.location.hash;
        const queryStart = hash.indexOf("?");
        if (queryStart !== -1) {
            const params = new URLSearchParams(hash.slice(queryStart));
            const id = params.get("_id") || params.get("quoteId");
            if (id) return id;
        }
        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.get("_id") || searchParams.get("quoteId");
    };

    const quoteId = getQuoteIdFromUrl();

    const proceed = () => {
        dispatch(stepActions.onRestartCurrentStepToDefault());
        sessionStorage.setItem("payment-screen", "payment-opt");
        sessionStorage.removeItem("currentStep");
        navigate("/policies");
    };

    const verifyPayment = async () => {
        if (!quoteId) return;

        setStatus("verifying");
        setErrorMessage(null);

        try {
            const result = await makePostRequest(
                `${process.env.REACT_APP_CORE_PAYMENTS_URL}/ethiopia/chapa/verify`,
                { _id: quoteId },
                {
                    SKEY,
                    ModuleID: `${tenantCreds?.moduleId}`,
                    apiKey: `${tenantCreds?.moduleId}`,
                }
            );

            if (result && (result.Status === 200 || result.Status === 204)) {
                if (mixpanel.config.token) {
                    mixpanel.track("Travel|Payment|chapa_verified", {
                        quoteId,
                        moduleId: tenantCreds?.moduleId,
                    });
                }
                setStatus("success");
            } else {
                const msg = typeof result?.Payload === "string" 
                    ? result.Payload 
                    : result?.Message || t("paymentConfirmation.verificationFailed", "Payment verification failed. Please try again.");
                setErrorMessage(msg);
                setStatus("failed");
            }
        } catch (error: any) {
            console.error(error);
            setErrorMessage(error?.toString() || t("paymentConfirmation.verificationFailed", "Payment verification failed. Please try again."));
            setStatus("failed");
        }
    };

    useEffect(() => {
        if (quoteId) {
            verifyPayment();
        } else {
            setStatus("success");
        }
    }, [quoteId]);

    useEffect(() => {
        if (mixpanel.config.token) {
            mixpanel.track("PageView", {
                pageName: "Travel|Payment-success",
            });
        }
    }, [mixpanel]);

    if (status === "verifying") {
        return (
            <div className="container pt-5">
                <Header />
                <div className="paym-confirmation-container px-2 mt-4">
                    <div className="paym-confirmation-wapper" style={{ textAlign: "center", padding: "40px 20px" }}>
                        <Loader />
                        <div className="paym-text-sm text-bold mt-4">
                            {t("paymentConfirmation.verifyingTitle", "Verifying Payment...")}
                        </div>
                        <div className="paym-text mt-2">
                            {t("paymentConfirmation.verifyingMsg", "We are securely verifying your payment status. Please hold on.")}
                        </div>
                    </div>
                </div>
                <br />
                <br />
            </div>
        );
    }

    if (status === "failed") {
        return (
            <div className="container pt-5">
                <Header />
                <div className="paym-confirmation-container px-2 mt-4">
                    <div className="paym-confirmation-wapper" style={{ textAlign: "center", padding: "40px 20px" }}>
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 200, damping: 18 }}
                            style={{
                                width: 80,
                                height: 80,
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #ef4444, #f87171)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto 28px",
                                boxShadow: "0 8px 32px rgba(239,68,68,0.25)",
                            }}
                        >
                            <span style={{ fontSize: "32px", color: "#fff", lineHeight: 1 }}>⚠️</span>
                        </motion.div>

                        <div className="paym-text-sm text-bold" style={{ color: "#b91c1c" }}>
                            {t("paymentConfirmation.failedTitle", "Verification Failed")}
                        </div>
                        <br />
                        <div className="paym-text" style={{ color: "#7f1d1d" }}>
                            {errorMessage}
                        </div>

                        <div className="mt-5 d-flex justify-content-center gap-3">
                            <button 
                                className="pointer-btn" 
                                style={{ background: "#ef4444", color: "#fff" }} 
                                onClick={verifyPayment}
                            >
                                {t("paymentConfirmation.retryBtn", "Retry Verification")}
                            </button>
                        </div>
                    </div>
                </div>
                <br />
                <br />
            </div>
        );
    }

    return (
        <div className="container pt-5">
            <Header />
            <div className="paym-confirmation-container px-2 mt-4">
                <div className="paym-confirmation-wapper" style={{ textAlign: "center" }}>
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 18, delay: 0.1 }}
                        style={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #00c853, #69f0ae)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 28px",
                            boxShadow: "0 8px 32px rgba(0,200,83,0.25)",
                        }}
                    >
                        <svg width="38" height="38" viewBox="0 0 38 38" fill="none" style={{ border: "unset !important" }}>
                            <motion.path
                                d="M9 19.5L16 26.5L29 13"
                                stroke="white"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                            />
                        </svg>
                    </motion.div>

                    <div className="paym-text-sm text-bold">{t("paymentConfirmation.successTitle")}</div>
                    <br />
                    <div className="paym-text">
                        {t("paymentConfirmation.successMsg")}
                    </div>

                    <div className="mt-5 d-flex justify-content-center" onClick={proceed}>
                        <button className="pointer-btn">{t("paymentConfirmation.viewMyPolicy")}</button>
                    </div>
                </div>
            </div>
            <br />
            <br />
        </div>
    );
}

export default Callback;